import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Form, Input, Select, Button, Row, Col, Card, Typography, message, Tooltip } from 'antd';
import { InfoCircleOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import AESUtil from '../utils/cryptoUtils';
import HistoryUtils from '../utils/historyUtils';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

// 动画变体定义
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { 
      staggerChildren: 0.07,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1,
    transition: { 
      type: "spring",
      stiffness: 300,
      damping: 24
    }
  }
};

const resultVariants = {
  hidden: { opacity: 0, height: 0 },
  visible: { 
    opacity: 1, 
    height: 'auto',
    transition: { 
      type: "spring",
      stiffness: 200,
      damping: 20
    }
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: { duration: 0.2 }
  }
};

const AESForm = forwardRef((props, ref) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedMode, setSelectedMode] = useState('CBC');
  const [copied, setCopied] = useState(false);
  const [encryptConfig, setEncryptConfig] = useState({
    action: 'encrypt',
    encryptMode: 'CBC',
    paddingMode: 'PKCS7',
    keyLength: 128,
    outputFormat: 'base64'
  });
  const [inputText, setInputText] = useState('');
  const [keyHelpText, setKeyHelpText] = useState(`AES-128需要16字节(128位)的密钥`);
  
  // 检查是否支持localStorage
  const isLocalStorageAvailable = () => {
    try {
      const testKey = '__test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      messageApi.error('您的浏览器不支持或已禁用本地存储，历史记录功能将不可用');
      return false;
    }
  };

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    // 设置表单数据的方法
    setFormValues: (values) => {
      // 设置表单字段值
      form.setFieldsValue({
        encryptMode: values.encryptMode || 'CBC',
        paddingMode: values.paddingMode,
        keyLength: values.keyLength,
        key: values.key,
        iv: values.iv,
        inputText: values.inputText
      });
      
      // 更新加密模式状态
      setSelectedMode(values.encryptMode || 'CBC');
      
      // 清除结果区域
      setResult('');
    }
  }));

  // 监听密钥长度变化，更新提示文本
  useEffect(() => {
    // 获取当前密钥长度
    const keyLength = form.getFieldValue('keyLength') || 128;
    const bytesNeeded = keyLength / 8;
    setKeyHelpText(`AES-${keyLength}需要${bytesNeeded}字节(${keyLength}位)的密钥`);
    
    // 重新验证密钥字段，如果已有值
    const currentKey = form.getFieldValue('key');
    if (currentKey) {
      form.validateFields(['key']);
    }
  }, [form.getFieldValue('keyLength')]);

  // 处理加密模式变更
  const handleModeChange = (mode) => {
    setSelectedMode(mode);
    // 如果切换到不需要IV的模式，清空IV输入
    if (!AESUtil.requiresIV(mode)) {
      form.setFieldValue('iv', '');
    }
  };

  // 校验密钥和IV格式
  const validateInputs = (values) => {
    const { key, iv, keyLength, encryptMode } = values;
    
    // 验证密钥长度
    if (!AESUtil.validateKeyLength(key, keyLength)) {
      const requiredLength = parseInt(keyLength) / 8;
      messageApi.error(`密钥长度错误：AES-${keyLength}需要${requiredLength}字节(${keyLength}位)的密钥`);
      return false;
    }
    
    // 如果当前模式需要IV，则验证IV长度
    if (AESUtil.requiresIV(encryptMode) && !AESUtil.validateIVLength(iv)) {
      messageApi.error('初始向量长度错误：需要16字节(128位)的IV');
      return false;
    }
    
    return true;
  };

  // 加密方法
  const handleEncrypt = () => {
    // 先检查localStorage是否可用
    const localStorageAvailable = isLocalStorageAvailable();
    
    form.validateFields().then(values => {
      const { inputText, key, iv, paddingMode, keyLength, encryptMode } = values;
      
      // 验证输入参数
      if (!validateInputs(values)) {
        return;
      }
      
      setLoading(true);
      
      try {
        // 调用AES加密
        const encryptedText = AESUtil.encrypt(inputText, key, iv, paddingMode, encryptMode);
        setResult(encryptedText);
        
        // 保存到历史记录
        if (localStorageAvailable) {
          const success = HistoryUtils.addHistory({
            type: '加密',
            encryptMode,
            paddingMode,
            keyLength,
            key,
            iv: iv || '无',
            inputText,
            result: encryptedText
          });
          
          if (success) {
            messageApi.success('加密成功，已保存到历史记录');
            // 触发历史记录更新 - 如果props有提供回调函数
            if (props.onHistoryUpdate && typeof props.onHistoryUpdate === 'function') {
              props.onHistoryUpdate();
            }
          } else {
            messageApi.success('加密成功，但历史记录保存失败');
          }
        } else {
          messageApi.success('加密成功');
        }
      } catch (error) {
        messageApi.error(`加密失败: ${error.message}`);
        console.error('加密错误:', error);
      } finally {
        setLoading(false);
      }
    }).catch(info => {
      console.log('验证失败:', info);
    });
  };

  // 解密方法
  const handleDecrypt = () => {
    // 先检查localStorage是否可用
    const localStorageAvailable = isLocalStorageAvailable();
    
    form.validateFields().then(values => {
      const { inputText, key, iv, paddingMode, keyLength, encryptMode } = values;
      
      // 验证输入参数
      if (!validateInputs(values)) {
        return;
      }
      
      setLoading(true);
      
      try {
        // 调用AES解密
        const decryptedText = AESUtil.decrypt(inputText, key, iv, paddingMode, encryptMode);
        setResult(decryptedText);
        
        // 保存到历史记录
        if (localStorageAvailable) {
          const success = HistoryUtils.addHistory({
            type: '解密',
            encryptMode,
            paddingMode,
            keyLength,
            key,
            iv: iv || '无',
            inputText,
            result: decryptedText
          });
          
          if (success) {
            messageApi.success('解密成功，已保存到历史记录');
            // 触发历史记录更新 - 如果props有提供回调函数
            if (props.onHistoryUpdate && typeof props.onHistoryUpdate === 'function') {
              props.onHistoryUpdate();
            }
          } else {
            messageApi.success('解密成功，但历史记录保存失败');
          }
        } else {
          messageApi.success('解密成功');
        }
      } catch (error) {
        messageApi.error(`解密失败: ${error.message}`);
        console.error('解密错误:', error);
      } finally {
        setLoading(false);
      }
    }).catch(info => {
      console.log('验证失败:', info);
    });
  };

  // 重置表单
  const handleReset = () => {
    form.resetFields();
    setResult('');
    setSelectedMode('CBC');
    messageApi.success('表单已重置');
  };

  // 复制结果
  const handleCopy = () => {
    navigator.clipboard.writeText(result)
      .then(() => {
        setCopied(true);
        messageApi.success('已复制到剪贴板');
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        messageApi.error('复制失败');
        console.error('复制失败:', err);
      });
  };

  // 处理配置变化
  const handleConfigChange = (changedValues, allValues) => {
    setEncryptConfig(prev => ({ ...prev, ...changedValues }));
    
    // 如果密钥长度改变，则更新提示
    if (changedValues.keyLength) {
      const bytesNeeded = changedValues.keyLength / 8;
      setKeyHelpText(`AES-${changedValues.keyLength}需要${bytesNeeded}字节(${changedValues.keyLength}位)的密钥`);
    }
  };

  return (
    <motion.div 
      className="aes-form"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {contextHolder}
      <Card>
        <motion.div variants={itemVariants}>
          <Title level={4}>AES 加密/解密</Title>
        </motion.div>
        
        <Form
          form={form}
          layout="vertical"
          name="aesForm"
          onValuesChange={handleConfigChange}
          initialValues={{
            action: 'encrypt',
            encryptMode: 'CBC',
            paddingMode: 'PKCS7',
            keyLength: 128,
            outputFormat: 'base64'
          }}
        >
          {/* 参数选择区域 */}
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <motion.div variants={itemVariants}>
                <Form.Item
                  name="encryptMode"
                  label={
                    <span>
                      加密模式&nbsp;
                      <Tooltip title={AESUtil.getModeDescription(selectedMode)}>
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                    </span>
                  }
                  rules={[{ required: true, message: '请选择加密模式' }]}
                >
                  <Select onChange={handleModeChange}>
                    {Object.entries(AESUtil.MODES).map(([key, value]) => (
                      <Option key={key} value={key}>
                        {value.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </motion.div>
            </Col>
            <Col xs={24} sm={8}>
              <motion.div variants={itemVariants}>
                <Form.Item
                  name="paddingMode"
                  label="填充模式"
                  rules={[{ required: true, message: '请选择填充模式' }]}
                >
                  <Select>
                    <Option value="PKCS7">PKCS7填充</Option>
                    <Option value="Zero">Zero填充</Option>
                    <Option value="NoPadding">不填充</Option>
                  </Select>
                </Form.Item>
              </motion.div>
            </Col>
            <Col xs={24} sm={8}>
              <motion.div variants={itemVariants}>
                <Form.Item
                  name="keyLength"
                  label="密钥长度"
                  rules={[{ required: true, message: '请选择密钥长度' }]}
                >
                  <Select>
                    <Option value="128">128位</Option>
                    <Option value="192">192位</Option>
                    <Option value="256">256位</Option>
                  </Select>
                </Form.Item>
              </motion.div>
            </Col>
          </Row>

          {/* 密钥和IV输入 */}
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <motion.div variants={itemVariants}>
                <Form.Item
                  name="key"
                  label="密钥"
                  rules={[
                    { required: true, message: '请输入密钥' },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        
                        const keyLength = form.getFieldValue('keyLength') || 128;
                        const bytesNeeded = keyLength / 8;
                        const actualBytes = new TextEncoder().encode(value).length;
                        
                        if (actualBytes !== bytesNeeded) {
                          return Promise.reject(`当前密钥长度为${actualBytes}字节，但AES-${keyLength}需要${bytesNeeded}字节，你可以尝试选择更大的密钥长度`);
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                  extra={keyHelpText}
                >
                  <Input placeholder="请输入密钥" maxLength={32} />
                </Form.Item>
              </motion.div>
            </Col>
            <Col xs={24} sm={12}>
              <motion.div variants={itemVariants}>
                <Form.Item
                  name="iv"
                  label={
                    <span>
                      初始向量 (IV)&nbsp;
                      <Tooltip title={AESUtil.requiresIV(selectedMode) ? '当前模式需要IV' : '当前模式不需要IV'}>
                        <InfoCircleOutlined style={{ color: AESUtil.requiresIV(selectedMode) ? '#ff4d4f' : '#1890ff' }} />
                      </Tooltip>
                    </span>
                  }
                  rules={[
                    { 
                      required: AESUtil.requiresIV(selectedMode), 
                      message: '请输入初始向量' 
                    }
                  ]}
                  extra="必须是16字节(128位)"
                >
                  <Input 
                    placeholder={AESUtil.requiresIV(selectedMode) ? "请输入初始向量" : "当前模式不需要IV"} 
                    disabled={!AESUtil.requiresIV(selectedMode)}
                    maxLength={16}
                  />
                </Form.Item>
              </motion.div>
            </Col>
          </Row>

          {/* 输入文本域 */}
          <motion.div variants={itemVariants}>
            <Form.Item
              name="inputText"
              label="输入文本"
              rules={[{ required: true, message: '请输入需要处理的文本' }]}
            >
              <TextArea 
                rows={4} 
                placeholder="请输入需要加密或解密的文本" 
              />
            </Form.Item>
          </motion.div>

          {/* 操作按钮 */}
          <motion.div variants={itemVariants}>
            <Form.Item>
              <div style={{ display: 'flex', gap: '8px' }}>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button 
                    type="primary" 
                    onClick={handleEncrypt}
                    loading={loading && result === ''}
                  >
                    加密
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button 
                    onClick={handleDecrypt}
                    loading={loading && result === ''}
                  >
                    解密
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button 
                    onClick={handleReset}
                    disabled={loading}
                  >
                    重置
                  </Button>
                </motion.div>
              </div>
            </Form.Item>
          </motion.div>
        </Form>

        {/* 结果显示区域 */}
        <AnimatePresence>
          {result && (
            <motion.div
              key="result"
              variants={resultVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              layout
            >
              <div style={{ marginTop: '24px' }}>
                <motion.div 
                  initial={{ opacity: 0, y: 10 }} 
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <Text strong>结果:</Text>
                </motion.div>
                <div 
                  style={{ 
                    padding: '12px', 
                    background: '#f5f5f5', 
                    borderRadius: '4px',
                    marginTop: '8px',
                    position: 'relative'
                  }}
                >
                  <motion.pre 
                    style={{ 
                      margin: 0, 
                      whiteSpace: 'pre-wrap', 
                      wordBreak: 'break-all'
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    {result}
                  </motion.pre>
                  <motion.div 
                    style={{ 
                      position: 'absolute', 
                      top: '8px', 
                      right: '8px' 
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button
                      type="text"
                      icon={copied ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CopyOutlined />}
                      onClick={handleCopy}
                    />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  );
});

export default AESForm; 